import React, { useMemo, useState, useEffect } from 'react'
import {
  Icon,
  InfoPill,
  MorpheusApplicationContext,
  ShallowTaskWorkflow as ShallowTaskWorkflowComponent,
  Text,
  coreConstants,
} from '@adc/ui-morpheus'
import classNames from 'classnames/bind'
import { workflow } from '@adc/blueprint'
import * as graph from '@adc/sdk'
import type { WorkflowResource } from '@adc/sdk/types'
import {
  groupTaskGroupsByWorkflowType,
  getDomainStatus,
  WORKFLOW_TYPE_ICONS,
  WORKFLOW_TYPE_LABELS,
} from '../workflowUtils'
import styles from './styles.scss'

const { ColorNames } = coreConstants
const cx = classNames.bind(styles)

interface WorkflowStateSectionProps {
  /** Primary context URN — listing or transaction */
  contextUrn: string | null
  /** All context source URNs — may include associated listing URN */
  contextSources?: string[]
}

const WorkflowStateSection: React.FC<WorkflowStateSectionProps> = ({
  contextUrn,
  contextSources = [],
}) => {
  const [collapsedTypes, setCollapsedTypes] = useState<Set<string>>(new Set())
  const { graphWebsocket } = MorpheusApplicationContext.useApplicationContext()

  // Prefer transaction URN (superset) over listing URN for workflow lookup.
  // Only fall back to listing URN when no transaction context is available.
  const workflowUrn = useMemo(() => {
    const transactionUrn = contextSources.find(src =>
      src.startsWith('adc:transaction:')
    )
    if (transactionUrn) return transactionUrn
    return contextSources.find(src => src.startsWith('adc:listing:')) ?? null
  }, [contextSources])

  const urns = useMemo(() => (workflowUrn ? [workflowUrn] : []), [workflowUrn])

  const { data: workflowEntries } = workflow.hooks.useWorkflows({
    urns,
    workflowTypes: [
      graph.enums.ShallowTaskWorkflowType.Intake,
      graph.enums.ShallowTaskWorkflowType.MarketplaceActivation,
      graph.enums.ShallowTaskWorkflowType.Selling,
      graph.enums.ShallowTaskWorkflowType.Closing,
    ],
    graphWebsocket,
    enabled: urns.length > 0,
  })

  const combinedWorkflow = useMemo(() => {
    const raw = workflowEntries?.raw
    if (!raw || raw.length === 0) return null
    return raw[0]?.workflow ?? null
  }, [workflowEntries?.raw])

  const grouped = useMemo(
    () => groupTaskGroupsByWorkflowType(combinedWorkflow),
    [combinedWorkflow]
  )

  // Auto-collapse non-ongoing workflows, expand ongoing ones.
  const groupedTypesKey = grouped.map(g => g.workflowType).join(',')
  useEffect(() => {
    if (grouped.length === 0) return
    const collapsed = new Set<string>()
    for (const { workflowType, taskGroups } of grouped) {
      const status = getDomainStatus(taskGroups)
      if (status !== 'ongoing') {
        collapsed.add(workflowType)
      }
    }
    setCollapsedTypes(collapsed)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedTypesKey])

  const isLoading = urns.length > 0 && workflowEntries.raw.length === 0

  // Hide section if no URN, or done loading with no workflow
  if (!contextUrn || (!isLoading && !combinedWorkflow)) {
    return null
  }

  const toggleCollapse = (workflowType: string): void => {
    setCollapsedTypes(prev => {
      const next = new Set(prev)
      if (next.has(workflowType)) {
        next.delete(workflowType)
      } else {
        next.add(workflowType)
      }
      return next
    })
  }

  return (
    <div className={cx('collapsible-section')}>
      <div className={cx('workflow-state-header')}>
        <Text.Header level={3} compact>
          Workflow State
        </Text.Header>
        {combinedWorkflow?.current_status_label && (
          <Text color='gray' size='small'>
            {combinedWorkflow.current_status_label}
          </Text>
        )}
      </div>

      {isLoading ? (
        <Text color='gray' size='tiny'>
          Loading workflow…
        </Text>
      ) : (
        <div className={cx('workflow-domains')}>
          {grouped.map(({ workflowType, taskGroups }) => {
            const domainStatus = getDomainStatus(taskGroups)
            const isCollapsed = collapsedTypes.has(workflowType)
            const label = WORKFLOW_TYPE_LABELS[workflowType] || workflowType
            const iconName = WORKFLOW_TYPE_ICONS[workflowType] || 'circle-nodes'

            const resource: WorkflowResource = {
              __typename: 'Listing',
              workflow: {
                ...combinedWorkflow!,
                task_groups: taskGroups,
              },
            }

            return (
              <div
                key={workflowType}
                className={cx('workflow-domain', {
                  'workflow-domain--active': domainStatus === 'ongoing',
                })}
              >
                <div
                  className={cx('workflow-domain-header')}
                  onClick={() => toggleCollapse(workflowType)}
                  role='button'
                  tabIndex={0}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      toggleCollapse(workflowType)
                    }
                  }}
                  data-elm-id={`workflow_domain_${workflowType}`}
                >
                  <div className={cx('workflow-domain-header-left')}>
                    <Icon
                      name={iconName}
                      iconStyle='solid'
                      size='small'
                      color={
                        domainStatus === 'ongoing'
                          ? ColorNames.Action
                          : ColorNames.Gray
                      }
                      elmId={`workflow_icon_${workflowType}`}
                    />
                    <Text
                      weight='heavy'
                      size='small'
                      color={
                        domainStatus === 'ongoing'
                          ? ColorNames.Action
                          : undefined
                      }
                    >
                      {label}
                    </Text>
                    {domainStatus === 'ongoing' && (
                      <InfoPill
                        content='Ongoing'
                        size='x-small'
                        backgroundColor={ColorNames.ActionLight}
                        foregroundColor={ColorNames.Action}
                        elmId={`workflow_pill_${workflowType}`}
                      />
                    )}
                    {domainStatus === 'complete' && (
                      <InfoPill
                        content='Complete'
                        size='x-small'
                        backgroundColor={ColorNames.SuccessLight}
                        foregroundColor={ColorNames.Success}
                        elmId={`workflow_pill_${workflowType}`}
                      />
                    )}
                  </div>
                  <Icon
                    name={isCollapsed ? 'chevron-up' : 'chevron-down'}
                    iconStyle='solid'
                    size='small'
                    color='gray'
                    elmId={`workflow_chevron_${workflowType}`}
                  />
                </div>

                {!isCollapsed && (
                  <div className={cx('workflow-domain-content')}>
                    <ShallowTaskWorkflowComponent resource={resource} compact />
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default WorkflowStateSection
